import { useForm } from "react-hook-form";
import { CircleAlert, PlusCircle } from "lucide-react";
import { PopoverTooltip } from "@/components/ui/tooltip";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import FilePond from "@/components/ui/file-pond";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import ImageUploadPreview from "@/components/ui/image-upload-preview";

import {
  candidateExperienceFormSchema,
  CandidateExperienceFormSchema,
} from "./schema";
import {
  engagementTypeList,
  functionAreaList,
  IExperience,
  industryList,
  stageList,
} from "@/redux/features/candidateProfileSlice";

import { useAddUpdateExperience } from "@/mutations/candidate/profile/experience/add-update-experience";
import { useDeleteExperience } from "@/mutations/candidate/profile/experience/delete-experience";
import dateStringToLocalDate from "@/utils/dateStringToLocalDate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactDatePicker from "react-datepicker";
import { CalendarSVG } from "@/assets/svgs";
import { Textarea } from "@/components/ui/textarea";

type Props =
  | {
      profileId: string;
      experienceId: string;
      experience: IExperience;
    }
  | {
      profileId: string;
      experienceId?: never;
      experience?: never;
    };

const CandidateExperienceFormDialog = ({
  profileId,
  experienceId,
  experience,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { addExperience, updateExperience, isUpdatingExperience } =
    useAddUpdateExperience(profileId, () => setIsOpen(false));
  const { deleteExperience, isDeletingExperience } = useDeleteExperience(
    profileId,
    experienceId ?? "",
    () => setIsOpen(false),
  );

  const form = useForm<CandidateExperienceFormSchema>({
    resolver: zodResolver(candidateExperienceFormSchema),
    mode: "onChange",
    defaultValues: {
      companyName: experience?.companyName ?? "",
      companyDescription: experience?.companyDescription ?? "",
      industries:
        experience?.industries ??
        (experience?.otherIndustries ? "Other" : undefined),
      otherIndustries: experience?.otherIndustries ?? "",
      stage: experience?.stage,
      jobTitle: experience?.jobTitle ?? "",
      engagementType: experience?.engagementType,
      functions: experience?.functions,
      location: experience?.location ?? "",
      startDate: dateStringToLocalDate(experience?.startDate) ?? undefined,
      endDate: dateStringToLocalDate(experience?.endDate) ?? undefined,
      outcomes: experience?.outcomes ?? "",
    },
  });

  const { industries } = form.watch();

  useEffect(() => {
    form.reset({
      companyName: experience?.companyName ?? "",
      companyDescription: experience?.companyDescription ?? "",
      industries:
        experience?.industries ??
        (experience?.otherIndustries ? "Other" : undefined),
      otherIndustries: experience?.otherIndustries ?? "",
      stage: experience?.stage,
      jobTitle: experience?.jobTitle ?? "",
      engagementType: experience?.engagementType,
      functions: experience?.functions,
      location: experience?.location ?? "",
      startDate: dateStringToLocalDate(experience?.startDate) ?? undefined,
      endDate: dateStringToLocalDate(experience?.endDate) ?? undefined,
      outcomes: experience?.outcomes ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, experience]);

  const onSubmit = (data: CandidateExperienceFormSchema) => {
    if (experience && experienceId) {
      updateExperience({ data, experienceId });
    } else {
      addExperience({ data });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {experience ? (
          <Button variant="link" className="edit-text self-end">
            Edit
          </Button>
        ) : (
          <Button variant="secondary">
            <PlusCircle />
            Add An Experience
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>
              {experience ? "Edit Experience" : "Create An Experience"}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            {experience ? "Edit" : "Create"} your experience below.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          <Form {...form}>
            <form
              className="space-y-4 w-full"
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit((data) => onSubmit(data))();
              }}
            >
              <FormField
                control={form.control}
                name="jobTitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Position</FormLabel>
                    <FormControl>
                      <Input placeholder="CEO" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Company</FormLabel>
                    <FormControl>
                      <Input placeholder="Microsoft" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="companyLogo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Logo</FormLabel>
                    <ImageUploadPreview
                      newImageFile={field.value}
                      oldImageLink={experience?.companyLogoLink}
                    />
                    <FormControl>
                      <FilePond
                        value={field.value ? [field.value] : []}
                        accept={"image/*"}
                        maxFileSizeKiloBytes={10240}
                        onChange={(files) => {
                          field.onChange(files[0]);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="engagementType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Engagement Type</FormLabel>
                    <FormControl>
                      <Select
                        name={field.name}
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select one"} />
                        </SelectTrigger>
                        <SelectContent>
                          {engagementTypeList.map((v) => (
                            <SelectItem key={`engagement-type-${v}`} value={v}>
                              {v}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="functions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Function</FormLabel>
                    <FormControl>
                      <Select
                        name={field.name}
                        value={field.value?.[0]}
                        onValueChange={(v) => field.onChange(v && [v])}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select one"} />
                        </SelectTrigger>
                        <SelectContent>
                          {functionAreaList.map((v) => (
                            <SelectItem key={`function-type-${v}`} value={v}>
                              {v}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input placeholder="New York, NY" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-col gap-2 self-stretch items-stretch">
                <FormLabel>Dates of Employment</FormLabel>

                <div className="flex flex-row gap-2 justify-between">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ReactDatePicker
                            className="flex-1 py-3 px-2 border border-[#E8E8E8] rounded-xl"
                            selected={field.value}
                            onChange={field.onChange}
                            dateFormat="MM/yyyy"
                            showMonthYearPicker
                            showIcon
                            icon={<CalendarSVG className="top-[2px]" />}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <p className="m-auto">&mdash;</p>
                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ReactDatePicker
                            className="flex-1 py-3 px-2 border border-[#E8E8E8] rounded-xl"
                            selected={field.value}
                            onChange={field.onChange}
                            dateFormat="MM/yyyy"
                            showMonthYearPicker
                            showIcon
                            icon={<CalendarSVG className="top-[2px]" />}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <FormField
                control={form.control}
                name="outcomes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Responsibilities and Outcomes</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full h-[1px] min-h-[1px] bg-[#F2F2F2]" />
              <p className="text-sm font-semibold text-[#22234A]">
                More about your role & company
              </p>

              <FormField
                control={form.control}
                name="companyDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What does the company do?</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe it in one or two sentences"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="industries"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      What industry does the company operate in?
                    </FormLabel>
                    <span className="flex flex-row gap-0.5 text-xs text-gray-600">
                      Choose 1 industry from the list
                      <PopoverTooltip
                        content="If the company operates in multiple industries, choose
                          the primary one."
                      >
                        <CircleAlert className="cursor-pointer" size={12} />
                      </PopoverTooltip>
                    </span>
                    <FormControl>
                      <Select
                        name={field.name}
                        value={field.value}
                        onValueChange={(v) => {
                          field.onChange(v);

                          if (v !== "Other") {
                            form.setValue("otherIndustries", undefined);
                          }
                        }}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select one"} />
                        </SelectTrigger>
                        <SelectContent>
                          {industryList.map((v) => (
                            <SelectItem key={`function-type-${v}`} value={v}>
                              {v}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {industries === "Other" && (
                <FormField
                  control={form.control}
                  name="otherIndustries"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="Other industry" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="stage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      What stage was the company at when you hired?
                    </FormLabel>
                    <FormControl>
                      <Select
                        name={field.name}
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select one"} />
                        </SelectTrigger>
                        <SelectContent>
                          {stageList.map((v) => (
                            <SelectItem key={`stage-${v}`} value={v}>
                              {v}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex justify-between">
                <Button
                  variant="outline"
                  className="w-fit"
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                {experience && (
                  <Button
                    variant="destructive"
                    className="w-fit"
                    type="submit"
                    disabled={isDeletingExperience || isUpdatingExperience}
                    onClick={(e) => {
                      e.preventDefault();
                      deleteExperience();
                    }}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="secondary"
                  className="w-fit"
                  type="submit"
                  disabled={
                    !form.formState.isValid ||
                    isDeletingExperience ||
                    isUpdatingExperience
                  }
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateExperienceFormDialog;
